import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debugExpandTaxonomyKeys } from '../utils/api';
import { Heading } from '../components/ui/heading';

const Pill: React.FC<{ text: string; color?: string }> = ({ text, color }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${color || 'bg-gray-50 text-gray-700 border-gray-200'}`}>{text}</span>
);

const TaxonomyExpandDebug: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [taxKeysInput, setTaxKeysInput] = useState('');
  const [rcKeysInput, setRcKeysInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const taxKeys = useMemo(() => taxKeysInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean), [taxKeysInput]);
  const rcKeys = useMemo(() => rcKeysInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean), [rcKeysInput]);

  useEffect(() => {
    const tk = searchParams.get('taxonomyCategoryKeys');
    const rck = searchParams.get('rootCauseCategoryKeys');
    if (tk) setTaxKeysInput(tk);
    if (rck) setRcKeysInput(rck);
  }, [searchParams]);

  const run = async () => {
    try {
      setLoading(true);
      const data = await debugExpandTaxonomyKeys(taxKeys, rcKeys);
      setResult(data);
    } catch (e: any) {
      setResult({ error: e?.message || 'Failed to expand' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <Heading level={1} className="mb-2">Taxonomy Expansion Debug</Heading>
      <p className="text-gray-600 mb-4">Enter taxonomy_category_keys (dimension:category) to see how the backend expands them to label-id groups (AND across groups, OR within group).</p>
      <div className="space-y-3 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">taxonomy_category_keys (comma or space separated)</label>
          <input value={taxKeysInput} onChange={(e) => setTaxKeysInput(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="foreseeable_harm:financial_distress dissatisfaction:implicit" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">root_cause_category_keys (optional)</label>
          <input value={rcKeysInput} onChange={(e) => setRcKeysInput(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="root_causes:claims" />
        </div>
        <div className="flex gap-2">
          <button onClick={run} disabled={loading} className="px-4 py-2 rounded bg-[#4342FF] text-white disabled:opacity-50">{loading ? 'Expanding…' : 'Expand'}</button>
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          {result.error && <div className="text-red-600">{result.error}</div>}
          <div>
            <div className="text-sm text-gray-600">Input keys</div>
            <div className="flex flex-wrap gap-2 mt-1">
              {(result.taxonomy_category_keys || []).map((k: string, i: number) => <Pill key={`tk-${i}`} text={k} />)}
              {(result.root_cause_category_keys || []).map((k: string, i: number) => <Pill key={`rc-${i}`} text={k} />)}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold">Label groups (AND across groups)</div>
            {(result.groups_resolved || []).length === 0 && <div className="text-sm text-gray-500">None</div>}
            <div className="space-y-2 mt-2">
              {(result.groups_resolved || []).map((group: any[], gi: number) => (
                <div key={`g-${gi}`} className="border rounded p-2">
                  <div className="text-xs text-gray-500 mb-1">Group {gi + 1}</div>
                  <div className="flex flex-wrap gap-2">
                    {group.map((lab, li) => (
                      <Pill key={`g${gi}-${li}`} text={`${lab.label_name} · ${lab.dimension_name}/${lab.category_name}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold">Flat labels (legacy OR)</div>
            {(result.labels_resolved || []).length === 0 && <div className="text-sm text-gray-500">None</div>}
            <div className="flex flex-wrap gap-2 mt-2">
              {(result.labels_resolved || []).map((lab: any, li: number) => (
                <Pill key={`l-${li}`} text={`${lab.label_name} · ${lab.dimension_name}/${lab.category_name}`} />
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Raw JSON</div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">{JSON.stringify(result, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxonomyExpandDebug;

